import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Stops a page being left with unsaved work on it.
 *
 * Two ways out of a page, so two guards:
 *
 *  - Leaving the app — a refresh, a closed tab, a typed URL — is the browser's
 *    own `beforeunload` prompt. It cannot be styled and it does not need to be.
 *  - Leaving for another page in the app is a click on a link, so those are
 *    caught in the capture phase before the router sees them, and handed back
 *    as `pendingTo` for the page to put its own dialog in front of.
 *
 * The router's `useBlocker` would be the tidy way to do the second one, and it
 * is not available here: it needs a data router, and the app is mounted under
 * <BrowserRouter>. Swapping that out is a much larger change than this.
 *
 * The browser's back button is not covered. Blocking it means pushing a decoy
 * history entry and unpicking it afterwards, which goes wrong in ways that are
 * worse than the problem — a back press with unsaved changes loses them.
 *
 * A page also navigates on its own — a Back button calling navigate() is not a
 * link and no click listener will see it — so `requestLeave(to)` puts those
 * through the same dialog, and goes straight there when there is nothing to
 * lose.
 *
 * Usage:
 *   const guard = useUnsavedChangesGuard(isDirty);
 *   <Modal open={!!guard.pendingTo} onClose={guard.stay}>… onClick={guard.leave}
 */
export default function useUnsavedChangesGuard(isDirty) {
  const navigate = useNavigate();
  const [pendingTo, setPendingTo] = useState(null);

  useEffect(() => {
    if (!isDirty) return undefined;

    const onBeforeUnload = (event) => {
      event.preventDefault();
      // Older browsers need the assignment; none of them show the string.
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) return undefined;

    const onClick = (event) => {
      if (event.defaultPrevented) return;
      // A modified click opens a new tab or window, which leaves this one alone.
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const anchor = event.target?.closest?.('a[href]');
      if (!anchor) return;
      if (anchor.target && anchor.target !== '_self') return;
      if (anchor.hasAttribute('download')) return;

      const url = new URL(anchor.href, window.location.href);
      // Off-site links unload the page, which is the other guard's job.
      if (url.origin !== window.location.origin) return;

      const to = `${url.pathname}${url.search}${url.hash}`;
      const here = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (to === here) return;

      // Capture phase: the router's own click handler never runs, so nothing
      // has navigated by the time the dialog opens.
      event.preventDefault();
      event.stopPropagation();
      setPendingTo(to);
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [isDirty]);

  const requestLeave = useCallback((to) => {
    if (!isDirty) {
      navigate(to);
      return;
    }
    setPendingTo(to);
  }, [isDirty, navigate]);

  const stay = useCallback(() => setPendingTo(null), []);

  const leave = useCallback(() => {
    const to = pendingTo;
    setPendingTo(null);
    if (to) navigate(to);
  }, [navigate, pendingTo]);

  return { pendingTo, requestLeave, stay, leave };
}
